import { PlusIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import FieldsetContainer from '@/components/shared/FieldsetContainer';
import LimitedEditionWrapper from '@/components/shared/LimitedEditionWrapper';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button } from '@/components/ui/daisy';
import useCreateTextMaterial from '@/data/hook/book/useCreateTextMaterial';
import { bookType, MaterialType } from '@/models/Book';

import MaterialTypeSelector from './MaterialTypeSelector';
import PdfMaterialFormService from './PdfMaterialFormService';

type MaterialAddStep = 'closed' | 'selecting' | MaterialType;

type MaterialAddSectionProps = {
  lessonId: number;
  bookType: bookType;
  isLimitedEdition?: boolean;
};

export default function MaterialAddSection({
  lessonId,
  bookType,
  isLimitedEdition = false,
}: MaterialAddSectionProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.materialsTab',
  });
  const [step, setStep] = useState<MaterialAddStep>('closed');
  const { createTextMaterial, isPending } = useCreateTextMaterial({
    lessonId,
    bookType,
  });

  const handleContinue = (type: MaterialType) => {
    if (type === MaterialType.TEXT) return createTextMaterial();
    setStep(type);
  };

  const close = () => setStep('closed');

  return (
    <>
      <ConditionalRenderer condition={step === MaterialType.PDF_INTERACTIVE}>
        <PdfMaterialFormService
          lessonId={lessonId}
          bookType={bookType}
          onClose={close}
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={step === 'selecting'}>
        <MaterialTypeSelector
          bookType={bookType}
          onCancel={close}
          onContinue={handleContinue}
          isSubmitting={isPending}
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={step === 'closed'}>
        <FieldsetContainer>
          <LimitedEditionWrapper isLimitedEdition={isLimitedEdition}>
            <Button
              size="md"
              onClick={() => setStep('selecting')}
              className="bg-base-300"
              disabled={isLimitedEdition}
            >
              <PlusIcon className="w-4 h-4" />
              {t('addMaterial')}
            </Button>
          </LimitedEditionWrapper>
        </FieldsetContainer>
      </ConditionalRenderer>
    </>
  );
}
